// Currency configuration for multi-currency support.
// All product prices are stored in INR (base); conversion happens at display time.

export type CurrencyCode = "INR" | "USD" | "EUR" | "GBP" | "AUD" | "CAD" | "SGD" | "AED";

export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  name: string;
  locale: string;
  flag: string; // emoji flag for selector UI
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  INR: { code: "INR", symbol: "₹", name: "Indian Rupee", locale: "en-IN", flag: "🇮🇳" },
  USD: { code: "USD", symbol: "$", name: "US Dollar", locale: "en-US", flag: "🇺🇸" },
  EUR: { code: "EUR", symbol: "€", name: "Euro", locale: "en-IE", flag: "🇪🇺" },
  GBP: { code: "GBP", symbol: "£", name: "British Pound", locale: "en-GB", flag: "🇬🇧" },
  AUD: { code: "AUD", symbol: "A$", name: "Australian Dollar", locale: "en-AU", flag: "🇦🇺" },
  CAD: { code: "CAD", symbol: "C$", name: "Canadian Dollar", locale: "en-CA", flag: "🇨🇦" },
  SGD: { code: "SGD", symbol: "S$", name: "Singapore Dollar", locale: "en-SG", flag: "🇸🇬" },
  AED: { code: "AED", symbol: "د.إ", name: "UAE Dirham", locale: "en-AE", flag: "🇦🇪" },
};

export const SUPPORTED_CURRENCY_CODES: CurrencyCode[] = [
  "INR", "USD", "EUR", "GBP", "AUD", "CAD", "SGD", "AED",
];

// Fallback rates if DB/edge function is unavailable. INR -> target.
export const FALLBACK_RATES: Record<CurrencyCode, number> = {
  INR: 1,
  USD: 0.012,
  EUR: 0.011,
  GBP: 0.0094,
  AUD: 0.018,
  CAD: 0.017,
  SGD: 0.016,
  AED: 0.044,
};

const LOCALE_TO_CURRENCY: Record<string, CurrencyCode> = {
  IN: "INR",
  US: "USD",
  GB: "GBP",
  IE: "EUR", FR: "EUR", DE: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", PT: "EUR", BE: "EUR",
  AU: "AUD",
  CA: "CAD",
  SG: "SGD",
  AE: "AED",
};

export function detectCurrencyFromBrowser(): CurrencyCode {
  if (typeof navigator === "undefined") return "INR";
  try {
    const lang = navigator.language || "en-IN";
    const region = lang.split("-")[1]?.toUpperCase();
    if (region && LOCALE_TO_CURRENCY[region]) return LOCALE_TO_CURRENCY[region];
  } catch {
    // ignore
  }
  return "INR";
}

/**
 * Convert an INR-denominated amount to the target currency and format it for display.
 * Uses Intl.NumberFormat for locale-aware formatting.
 */
export function formatCurrency(
  amountInInr: number,
  currency: CurrencyCode,
  rate: number,
  options: { maximumFractionDigits?: number } = {}
): string {
  const converted = amountInInr * rate;
  const cfg = CURRENCIES[currency];
  const maxDigits = options.maximumFractionDigits ?? (currency === "INR" ? 0 : 2);
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: "currency",
      currency: cfg.code,
      maximumFractionDigits: maxDigits,
      minimumFractionDigits: 0,
    }).format(converted);
  } catch {
    return `${cfg.symbol}${converted.toFixed(maxDigits)}`;
  }
}
